// App.js
import React, { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Products from './components/Products';
import BasedProductItems from './components/BasedProductItems';
import CustomProductItems from './components/CustomProductItems';
import AccountSettings from './components/AccountSettings';
import StockManagement from './components/StockManagement';
import ProductFields from './components/ProductFields';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => {
    return localStorage.getItem('currentPage') || 'dashboard';
  });
  const [pageParams, setPageParams] = useState({});
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const token = getCookie('token');
    if (token) {
      setIsAuthenticated(true);
    }
    
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    if (savedTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Listen for auth error events from api.js
    window.addEventListener('auth-error', handleAuthError);

    return () => {
      window.removeEventListener('auth-error', handleAuthError);
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      localStorage.setItem('currentPage', currentPage);
    }
  }, [currentPage, isAuthenticated]);

  const handleAuthError = () => {
    // Clear authentication state
    setIsAuthenticated(false);
    // Clear token
    document.cookie = 'token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    // Clear stored page
    localStorage.removeItem('currentPage');
    // Reset page state
    setCurrentPage('dashboard');
    setPageParams({});
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  const handleLogout = () => {
    document.cookie = 'token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    localStorage.removeItem('currentPage');
    setIsAuthenticated(false);
    setCurrentPage('dashboard');
    setPageParams({});
  };

  const handlePageChange = (page, params = {}) => {
    setCurrentPage(page);
    setPageParams(params);
  };

  const renderPage = () => {
    switch(currentPage) {
      case 'dashboard':
        return <Dashboard />;
      case 'products':
        return <Products setCurrentPage={handlePageChange} />;
      case 'based-items':
        return <BasedProductItems />;
      case 'custom-items':
        return <CustomProductItems setCurrentPage={handlePageChange} />;
      case 'account':
        return <AccountSettings onLogout={handleLogout} />;
      case 'stock-management':
        return (
          <StockManagement 
            initialProductId={pageParams.productId}
            initialCustomItemId={pageParams.customItemId}
          />
        );
      case 'product-fields':
        return (
          <ProductFields 
            initialProductId={pageParams.productId}
          />
        );
      default:
        return <Dashboard />;
    }
  };

  if (!isAuthenticated) {
    return (
      <>
        <Toaster position="top-right" />
        <Login onLogin={() => setIsAuthenticated(true)} />
      </>
    );
  }

  return (
    <AuthProvider>
      <Toaster position="top-right" />
      <Layout 
        currentPage={currentPage}
        setCurrentPage={handlePageChange}
        onLogout={handleLogout}
        theme={theme}
        toggleTheme={toggleTheme}
      >
        {renderPage()}
      </Layout>
    </AuthProvider>
  );
}

export default App;